import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { useNotificationContext } from '../contexts/NotificationContext';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useAnimations } from '../hooks/useAnimations';
import SidebarLayout from '../components/layout/SidebarLayout';
import { 
  Dumbbell, 
  FileText, 
  PlusCircle, 
  History, 
  Plus, 
  Feather,
  Award,
  Heart,
  Star,
  Clock,
  CalendarClock,
  ArrowRightCircle,
  Smartphone
} from 'lucide-react';
import DashboardStats from '../components/Standalone/DashboardStats';
import QuickAccessWorkouts from '../components/Standalone/QuickAccessWorkouts';
import AIInsightPlaceholder from '../components/Standalone/AIInsightPlaceholder';
import SubscriptionBadge from '../components/Standalone/SubscriptionBadge';
import StandaloneWorkoutHistory from '../components/Standalone/StandaloneWorkoutHistory';
import ImprovedUserExerciseForm from '../components/Standalone/ImprovedUserExerciseForm';
import UserExerciseManager from '../components/Standalone/UserExerciseManager';

const UnifiedStandaloneDashboard = () => {
  const { currentUser, getAuthenticatedApi } = useAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotificationContext();
  const { handleError } = useErrorHandler();
  const { getAnimationClasses, getInteractionClasses } = useAnimations();
  
  // Stati per modali e visualizzazioni
  const [showHistory, setShowHistory] = useState(false);
  const [showAddExerciseForm, setShowAddExerciseForm] = useState(false);
  const [showExerciseManager, setShowExerciseManager] = useState(false);
  
  // Stati per dati utente
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState({
    workoutCount: 0,
    completedSets: 0,
    userSince: null,
    recentWorkouts: [],
    plans: []
  });

  useEffect(() => {
    fetchSubscription();
    fetchUserData();
  }, [currentUser]);

  // Refresh dati in background ogni 5 minuti
  const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
  const userId = currentUser?.user_id ?? null;
  useEffect(() => {
    if (userId == null) return;
    const interval = setInterval(() => {
      fetchSubscription(true);
      fetchUserData();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [userId]);

  const fetchSubscription = async (isBackgroundRefresh = false) => {
    try {
      if (!isBackgroundRefresh) setLoading(true);
      setError(null);
      const api = getAuthenticatedApi();
      
      const response = await api.get('/subscription_api.php?action=current_subscription');
      
      if (response.data.success && response.data.data?.subscription) {
        setSubscription(response.data.data.subscription);
      } else {
        setError(t('standaloneDashboard.subscription.loadingError'));
      }
    } catch (error) {
      console.error('Errore caricamento piano:', error);
      if (!isBackgroundRefresh) handleError(error, { context: 'Caricamento piano' });
      setError(t('standaloneDashboard.subscription.connectionError'));
    } finally {
      if (!isBackgroundRefresh) setLoading(false);
    }
  };

  const fetchUserData = async () => {
    if (!currentUser) return;
    
    try {
      const api = getAuthenticatedApi();
      
      // Fetch workout plans
      const plansResponse = await fetch(`/api/schede_standalone.php?user_id=${currentUser.user_id}`);
      const plansData = await plansResponse.json();
      
      // Fetch workout history
      const historyResponse = await fetch(`/api/get_allenamenti_standalone.php?user_id=${currentUser.user_id}`);
      const historyData = await historyResponse.json();
      
      // Calculate stats
      const workouts = historyData.success ? historyData.allenamenti : [];
      const completedWorkouts = workouts.filter(w => w.durata_totale !== null);
      
      let totalSets = 0;
      completedWorkouts.forEach(workout => {
        const duration = parseInt(workout.durata_totale) || 0;
        totalSets += Math.ceil(duration / 2);
      });
      
      const userSince = new Date();
      userSince.setDate(userSince.getDate() - Math.max(completedWorkouts.length * 2, 7));
      
      setUserData({
        workoutCount: completedWorkouts.length,
        completedSets: totalSets,
        userSince: userSince,
        recentWorkouts: completedWorkouts.slice(0, 5),
        plans: plansData.success ? plansData.schede : []
      });
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      handleError(error, { context: 'Caricamento dati dashboard' });
      setError(t('standaloneDashboard.errors.dataFetch'));
    }
  };

  // Get user's first name for greeting
  const getUserFirstName = () => {
    if (!currentUser) return '';
    
    if (currentUser.name) {
      return currentUser.name.split(' ')[0];
    }
    
    return currentUser.username;
  };

  // Se mostriamo modali, renderizzali
  if (showHistory) {
    return (
      <SidebarLayout>
        <StandaloneWorkoutHistory 
          onBack={() => setShowHistory(false)} 
        />
      </SidebarLayout>
    );
  }

  if (showExerciseManager) {
    return (
      <SidebarLayout>
        <UserExerciseManager
          onBack={() => setShowExerciseManager(false)}
        />
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="w-[95%] mx-auto px-4 py-6 space-y-8">
        {/* Header con saluto e subscription badge */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
              {t('standaloneDashboard.welcome')}, {getUserFirstName()}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {new Date().toLocaleDateString(undefined, { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <SubscriptionBadge />
            <Link
              to="/standalone/visualizza-schede"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
            >
              <PlusCircle className="h-4 w-4" />
              <span className="hidden sm:inline">{t('standaloneDashboard.workoutPlans.createNew')}</span>
              <span className="sm:hidden">{t('standaloneDashboard.createNew')}</span>
            </Link>
          </div>
        </div>

        {/* Card piano attuale utente */}
        <div className={`mb-6 min-h-[64px] ${getAnimationClasses('fadeIn')}`}>
          {loading ? (
            <div className="p-4 rounded-xl border flex items-center gap-4 shadow-sm animate-pulse bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <div className="h-8 w-8 rounded-full bg-gray-300 dark:bg-gray-700" />
              <div>
                <div className="font-bold text-lg text-gray-400 dark:text-gray-500">{t('standaloneDashboard.subscription.loading')}</div>
                <div className="text-sm text-gray-300 dark:text-gray-600">{t('standaloneDashboard.subscription.loadingDescription')}</div>
              </div>
            </div>
          ) : error ? (
            <div className="p-4 rounded-xl border flex items-center gap-4 shadow-sm bg-red-100 dark:bg-red-900 border-red-300 dark:border-red-700">
              <div className="h-8 w-8 rounded-full bg-red-300 dark:bg-red-700 flex items-center justify-center">
                <span className="text-red-700 dark:text-red-200 font-bold text-xl">!</span>
              </div>
              <div>
                <div className="font-bold text-lg text-red-700 dark:text-red-200">{error}</div>
                <div className="text-sm text-red-600 dark:text-red-300">{t('standaloneDashboard.subscription.connectionError')}</div>
              </div>
            </div>
          ) : subscription && (
            <div className={`p-4 rounded-xl border flex items-center gap-4 shadow-sm ${
              isDark
                ? subscription.price > 0
                  ? 'bg-indigo-900/20 border-indigo-800'
                  : 'bg-gray-800 border-gray-700'
                : subscription.price > 0
                  ? 'bg-indigo-50 border-indigo-200'
                  : 'bg-gray-50 border-gray-200'
            }`}>
              {subscription.price > 0 ? (
                <Award className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
              ) : (
                <Star className="h-8 w-8 text-gray-400 dark:text-gray-200" />
              )}
              <div>
                <div className="font-bold text-lg text-gray-900 dark:text-white">
                  {subscription.price > 0 ? t('standaloneDashboard.subscription.premiumPlan') : t('standaloneDashboard.subscription.freePlan')}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  {subscription.price > 0
                    ? t('standaloneDashboard.subscription.premiumDescription')
                    : t('standaloneDashboard.subscription.freeDescription')}
                </div>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* Placeholder per future funzionalità */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 rounded-lg border border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-3">
            <CalendarClock className="h-8 w-8 text-amber-600 dark:text-amber-400" />
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('standaloneDashboard.streakTracking')}</h3>
              <p className="text-sm text-gray-700 dark:text-gray-300">{t('standaloneDashboard.comingSoon')}</p>
            </div>
          </div>
        </div>

        {/* Stats cards */}
        <div className={getAnimationClasses('slideInLeft')}>
          <DashboardStats 
            loading={loading} 
            stats={{
              workoutCount: userData.workoutCount,
              completedSets: userData.completedSets,
              userSince: userData.userSince
            }} 
          />
        </div>

        {/* Layout principale - 2 colonne su desktop, 1 su mobile */}
        <div className="grid md:grid-cols-5 gap-6">
          {/* Sezione progressi - 3 colonne */}
          <div className={`md:col-span-3 space-y-6 ${getAnimationClasses('slideInLeft')}`}>
            {/* Messaggio App Mobile */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-800 rounded-lg">
                  <Smartphone className="h-6 w-6 text-blue-600 dark:text-blue-300" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Allenamenti sull'App Mobile
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300">
                Registra i tuoi workout direttamente dall'app mobile FitGymTrack per iOS e Android.
                Tieni traccia dei tuoi progressi ovunque ti alleni!
              </p>
            </div>
            
            {/* AI Insight placeholder */}
            <AIInsightPlaceholder />
          </div>
          
          {/* Sezione accesso rapido - 2 colonne */}
          <div className={`md:col-span-2 space-y-6 ${getAnimationClasses('slideInRight')}`}>
            <QuickAccessWorkouts 
              loading={loading} 
              workoutPlans={userData.plans} 
            />
          </div>
        </div>

        {/* Sezione azioni rapide - solo su mobile o come alternativa */}
        <div className="md:hidden grid grid-cols-1 gap-4">
          <div className="bg-gradient-to-br from-indigo-500 to-blue-600 text-white rounded-xl p-6 shadow-md">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-xl font-bold mb-2">{t('standaloneDashboard.workoutPlans.title')}</h2>
                <p className="text-sm opacity-90 mb-4">{t('standaloneDashboard.workoutPlans.description')}</p>
              </div>
              <FileText className="h-8 w-8 opacity-80" />
            </div>
            <div className="mt-2 space-y-3">
              <Link
                to="/standalone/crea-scheda"
                className="px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white font-medium flex items-center justify-center transition"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                {t('standaloneDashboard.workoutPlans.createNew')}
              </Link>
              <Link
                to="/standalone/visualizza-schede"
                className="px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white font-medium flex items-center justify-center transition"
              >
                <FileText className="h-4 w-4 mr-2" />
                {t('standaloneDashboard.workoutPlans.viewPlans')}
              </Link>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-xl p-6 shadow-md">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-xl font-bold mb-2">{t('standaloneDashboard.workouts.title')}</h2>
                <p className="text-sm opacity-90 mb-4">{t('standaloneDashboard.workouts.description')}</p>
              </div>
              <History className="h-8 w-8 opacity-80" />
            </div>
            <div className="mt-2 space-y-3">
              <button
                onClick={() => setShowHistory(true)}
                className="w-full px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white font-medium flex items-center justify-center transition"
              >
                <History className="h-4 w-4 mr-2" />
                {t('standaloneDashboard.workouts.history')}
              </button>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white rounded-xl p-6 shadow-md">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-xl font-bold mb-2">{t('standaloneDashboard.customExercises.title')}</h2>
                <p className="text-sm opacity-90 mb-4">{t('standaloneDashboard.customExercises.description')}</p>
              </div>
              <Feather className="h-8 w-8 opacity-80" />
            </div>
            <div className="mt-2 space-y-3">
              <button
                onClick={() => setShowAddExerciseForm(true)}
                className="w-full px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white font-medium flex items-center justify-center transition"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('standaloneDashboard.customExercises.addNew')}
              </button>
              <button
                onClick={() => setShowExerciseManager(true)}
                className="w-full px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white font-medium flex items-center justify-center transition"
              >
                <Dumbbell className="h-4 w-4 mr-2" />
                {t('standaloneDashboard.customExercises.manage')}
              </button>
            </div>
          </div>
        </div>

        {/* Suggerimenti */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-lg p-4 text-sm text-gray-600 dark:text-gray-300">
          <p className="font-medium mb-2">{t('standaloneDashboard.tips.title')}</p>
          <ul className="list-disc pl-5 space-y-1">
            {t('standaloneDashboard.tips.items', { returnObjects: true }).map((tip, index) => (
              <li key={index}>{tip}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Modal per creare un nuovo esercizio */}
      {showAddExerciseForm && (
        <ImprovedUserExerciseForm 
          onClose={() => setShowAddExerciseForm(false)} 
          onSuccess={() => {
            setShowAddExerciseForm(false);
            fetchUserData(); // Ricarica i dati
          }}
        />
      )}
    </SidebarLayout>
  );
};

export default UnifiedStandaloneDashboard;
